// LoginForm.js
import React from 'react';
import classNames from 'classnames';
import { SectionProps } from '../../utils/SectionProps';
import { Link, withRouter } from 'react-router-dom';
import SectionHeader from './partials/SectionHeader';
import Input from '../elements/Input';
import Button from '../elements/Button';
import Checkbox from '../elements/Checkbox';
import '../../assets/scss/settings/sections/_login-form.scss'; // import the CSS below (adjust path as needed)
import api from '../../api/http';
import { API_ENDPOINTS } from '../../api/endpoints';
import { TokenService } from '../../api/tokenService';
import { ERROR_MESSAGES } from '../../api/errorMsg';
import { withLanguage } from '../../context/LanguageContext';

const propTypes = {
  ...SectionProps.types,
};

const defaultProps = {
  ...SectionProps.defaults,
};

class LoginForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      loading: false,
      error: '',
      organizations: [],
      organizationId: '',
    };
  }

  handleChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleLogin = async (e) => {
    e.preventDefault();
    this.setState({ loading: true, error: "" });

    try {
      const payload = {
        email: this.state.email,
        password: this.state.password,
        ...(this.state.organizationId && {
          organizationId: Number(this.state.organizationId),
        }),
      };

      const res = await api.post(API_ENDPOINTS.LOGIN, payload);
      console.log("Login success:", res.data);

      const data = res.data;

      const accessToken =
        data?.data?.accessToken !== undefined
          ? data.data.accessToken
          : data.accessToken;

      const refreshToken =
        data?.data?.refreshToken !== undefined
          ? data.data.refreshToken
          : data.refreshToken;

      TokenService.setTokens(accessToken, refreshToken);

      this.props.history.replace("/");
    } catch (err) {
      let message = ERROR_MESSAGES.LOGIN.DEFAULT;

      if (err.response) {
        // Some reverse proxies normalize non-2xx status codes. The response body is
        // the authoritative signal that login needs an organization selection.
        const organizationChoices = err.response.data?.organizations;
        const organizationSelectionRequired =
          Array.isArray(organizationChoices) &&
          organizationChoices.length > 0 &&
          (err.response.status === 409 ||
            err.response.data?.error === 'Organization selection required' ||
            err.response.data?.message === 'Organization selection is required');

        if (organizationSelectionRequired) {
          this.setState({
            organizations: organizationChoices,
            organizationId: '',
            error: '利用する組織を選択してください。',
          });
          return;
        }
        if (err.response.status === 400) {
          message = ERROR_MESSAGES.LOGIN.INVALID_CREDENTIALS;
        } else if (err.response.status === 401) {
          message = ERROR_MESSAGES.LOGIN.UNVERIFIED_OR_SUSPENDED;
        }
      }

      this.setState({ error: message });
    } finally {
      this.setState({ loading: false });
    }
  };


  render() {
    const {
      className,
      topOuterDivider,
      bottomOuterDivider,
      topDivider,
      bottomDivider,
      hasBgColor,
      invertColor,
      ...props
    } = this.props;
    const { text = (ja) => ja } = this.props.languageContext || {};
    const organizationRoleLabels = {
      owner: text('所有者', 'Owner'),
      billing_admin: text('請求管理者', 'Billing administrator'),
      member: text('メンバー', 'Member'),
      viewer: text('閲覧者', 'Viewer'),
    };

    const outerClasses = classNames(
      'signin section',
      topOuterDivider && 'has-top-divider',
      bottomOuterDivider && 'has-bottom-divider',
      hasBgColor && 'has-bg-color',
      invertColor && 'invert-color',
      className
    );

    const innerClasses = classNames(
      'signin-inner section-inner',
      topDivider && 'has-top-divider',
      bottomDivider && 'has-bottom-divider'
    );

    const sectionHeader = {
      // short title as requested
      title: text('おかえりなさい。', 'Welcome back.'),
    };

    return (
      <section {...props} className={outerClasses}>
        <div className="container">
          <div className={innerClasses}>
            <SectionHeader
              tag="h1"
              data={sectionHeader}
              className="center-content signin-title"
            />

            <div className="tiles-wrap">
              <div className="tiles-item">
                <div className="tiles-item-inner login-form-container">
                  <form onSubmit={this.handleLogin}>
                    <fieldset>
                      <Input
                        type="email"
                        name="email"
                        placeholder={text('メールアドレス', 'Email address')}
                        value={this.state.email}
                        onChange={this.handleChange}
                        required
                      />

                      <div className="mb-12 mt-12">
                        <Input
                          type="password"
                          name="password"
                          placeholder={text('パスワード', 'Password')}
                          value={this.state.password}
                          onChange={this.handleChange}
                          required
                        />
                      </div>

                      {this.state.organizations.length > 0 && (
                        <div className="organization-login-field mb-12">
                          <label htmlFor="login-organization">{text('利用する組織', 'Organization')}</label>
                          <p>{text('サインインする組織を選択してください。', 'Select the organization you want to use.')}</p>
                          <select
                            id="login-organization"
                            className="organization-login-select"
                            name="organizationId"
                            value={this.state.organizationId}
                            onChange={this.handleChange}
                            required
                          >
                            <option value="">{text('組織を選択してください', 'Select an organization')}</option>
                            {this.state.organizations.map((organization) => (
                              <option key={organization.organizationId} value={organization.organizationId}>
                                {organization.name} — {organizationRoleLabels[organization.platformRole] || organization.platformRole}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {this.state.error && (
                        <div className="error-text mb-12 text-xs">
                          {this.state.error}
                        </div>
                      )}

                      <div className="mt-24 mb-32">
                        <Button color="primary" wide type="submit">
                          {this.state.loading ? text('処理中...', 'Signing in...') : text('サインイン', 'Sign in')}
                        </Button>
                      </div>

                      <div className="signin-footer mb-32">
                        <div className="footer-left">
                          <Checkbox>{text('ログイン状態を保持する', 'Keep me signed in')}</Checkbox>
                        </div>
                        <div className="footer-right">
                          <Link
                            to="/forgot-password"
                            className="func-link text-xs"
                          >
                            {text('パスワードをお忘れですか？', 'Forgot your password?')}
                          </Link>
                        </div>
                      </div>
                    </fieldset>
                  </form>

                  <div className="signin-bottom has-top-divider">
                    <div className="pt-32 text-xs center-content text-color-low no-wrap">
                      {text('アカウントをお持ちではありませんか？', "Don't have an account?")}&nbsp;
                      <Link to="/signup/" className="func-link">
                        {text('サインアップ', 'Sign up')}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    );
  }
}

LoginForm.propTypes = propTypes;
LoginForm.defaultProps = defaultProps;

export default withRouter(withLanguage(LoginForm));
